import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ChevronLeftIcon,
  DocumentArrowDownIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
  ClipboardDocumentCheckIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { useSelectedPatient } from '../hooks/useSelectedPatient';
import { useCertificateForm } from '../hooks/formHooks/useCertificateForm';
import { usePrescriptionForm } from '../hooks/formHooks/usePrescriptionForm';
import type { PrescriptionFormData } from '../hooks/formHooks/usePrescriptionForm';
import { usePdfGeneration } from '../hooks/usePdfGeneration';
import { listPatientCertificates, getCertificateDownloadUrl } from '../services/api/apis';
import type { CertificateRecord } from '../services/api/apis';
import { buildCertificateContentBox } from '../services/pdf/pdfInputBuilder';
import { getBase64FromUrl } from '../helpers';
import PdfViewerModal from '../components/PdfViewerModal';

// ─── Status styles ────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, string> = {
  pending:  'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  assigned: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  rejected: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  issued:   'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
};

// ─── Info field ───────────────────────────────────────────────────────────────

const InfoField: React.FC<{ label: string; value?: string | null; mono?: boolean; wide?: boolean }> = ({
  label, value, mono, wide,
}) => (
  <div className={wide ? 'col-span-2' : ''}>
    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">{label}</p>
    <p className={`text-sm font-medium text-gray-800 dark:text-gray-100 mt-0.5 ${mono ? 'font-mono' : ''}`}>
      {value || <span className="text-gray-400 italic font-normal">—</span>}
    </p>
  </div>
);

// ─── Card ─────────────────────────────────────────────────────────────────────

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
    <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
      <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">{title}</h3>
    </div>
    <div className="p-5 grid grid-cols-2 gap-4">{children}</div>
  </div>
);

// ─── Saved certificates list ──────────────────────────────────────────────────

const SavedCertificates: React.FC<{ docId: string; refreshKey: number }> = ({ docId, refreshKey }) => {
  type ListState = 'loading' | 'error' | 'ready' | 'empty';
  type RowState = 'idle' | 'loading' | 'error';

  const [listState, setListState] = useState<ListState>('loading');
  const [certs, setCerts] = useState<CertificateRecord[]>([]);
  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerError, setViewerError] = useState<string | null>(null);
  const [viewerTitle, setViewerTitle] = useState('');
  const [viewerFileName, setViewerFileName] = useState('');

  useEffect(() => {
    setListState('loading');
    listPatientCertificates(docId)
      .then((data) => {
        setCerts(data);
        setListState(data.length === 0 ? 'empty' : 'ready');
      })
      .catch(() => setListState('error'));
  }, [docId, refreshKey]);

  const handleView = async (cert: CertificateRecord) => {
    const fileName = `${cert.certificate_type.replace(/\s+/g, '_')}_${cert.certificate_id.slice(0, 8)}.pdf`;
    setViewerTitle(cert.certificate_type);
    setViewerFileName(fileName);
    setViewerUrl(null);
    setViewerError(null);
    setViewerOpen(true);
    try {
      const { download_url } = await getCertificateDownloadUrl(docId, cert.certificate_id);
      setViewerUrl(download_url);
    } catch (err) {
      setViewerError(err instanceof Error ? err.message : 'Failed to load PDF');
    }
  };

  const handleDownload = async (cert: CertificateRecord) => {
    setRowStates((prev) => ({ ...prev, [cert.certificate_id]: 'loading' }));
    try {
      const { download_url } = await getCertificateDownloadUrl(docId, cert.certificate_id);
      const a = document.createElement('a');
      a.href = download_url;
      a.download = `${cert.certificate_type.replace(/\s+/g, '_')}_${cert.certificate_id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setRowStates((prev) => ({ ...prev, [cert.certificate_id]: 'idle' }));
    } catch {
      setRowStates((prev) => ({ ...prev, [cert.certificate_id]: 'error' }));
    }
  };

  const fmtDate = (iso: string) => {
    try {
      return new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));
    } catch { return iso; }
  };

  return (
    <>
      <PdfViewerModal
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        url={viewerUrl}
        title={viewerTitle}
        error={viewerError}
        downloadFileName={viewerFileName}
      />

      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-3">
          Saved Certificates
        </p>
        {listState === 'loading' && (
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />Loading…
          </div>
        )}
        {listState === 'error' && (
          <p className="text-xs text-red-500">Failed to load saved certificates</p>
        )}
        {listState === 'empty' && (
          <p className="text-xs text-gray-400 italic">No certificates uploaded yet. Generate one above to save it.</p>
        )}
        {listState === 'ready' && (
          <div className="space-y-2">
            {certs.map((cert) => {
              const rs = rowStates[cert.certificate_id] ?? 'idle';
              return (
                <div
                  key={cert.certificate_id}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700"
                >
                  <DocumentArrowDownIcon className="w-4 h-4 text-blue-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
                      {cert.certificate_type}
                    </p>
                    <p className="text-xs text-gray-400">{fmtDate(cert.uploaded_at)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleView(cert)}
                      className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      View
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDownload(cert)}
                      disabled={rs === 'loading'}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-700 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {rs === 'loading' ? (
                        <ArrowPathIcon className="w-4 h-4 animate-spin" />
                      ) : rs === 'error' ? (
                        <ExclamationCircleIcon className="w-4 h-4 text-red-500" />
                      ) : (
                        <DocumentArrowDownIcon className="w-4 h-4" />
                      )}
                      {rs === 'error' ? 'Retry' : 'Download'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

// ─── Loading skeleton ─────────────────────────────────────────────────────────

const Skeleton: React.FC = () => (
  <div className="space-y-6 animate-pulse">
    <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded-xl" />
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-48 bg-gray-200 dark:bg-gray-700 rounded-xl" />
      ))}
    </div>
    <div className="h-72 bg-gray-200 dark:bg-gray-700 rounded-xl" />
  </div>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

export const CertificateViewPage: React.FC = () => {
  const { docId } = useParams<{ docId: string }>();
  const navigate = useNavigate();

  const {
    patient,
    isLoading,
    error,
    initialFormData,
    submittedPrescription,
  } = useSelectedPatient(docId);

  const [includePrescription, setIncludePrescription] = useState(false);
  const [certRefreshKey, setCertRefreshKey] = useState(0);

  const handleUploaded = useCallback(() => setCertRefreshKey((k) => k + 1), []);

  const {
    certTemplateReady,
    rxTemplateReady,
    isLoadingTemplates,
    templateError,
    isGenerating,
    generateError,
    uploadState,
    uploadError,
    pendingUpload,
    fetchTemplates,
    generateCertificatePdf,
    generateMergedPdf,
    confirmUpload,
    discardUpload,
  } = usePdfGeneration({ onUploaded: handleUploaded });

  // Certificate form state (read-only: persistDraft=false, no submit)
  const { formData, setField } = useCertificateForm({
    patientDocId: docId!,
    initialData: initialFormData,
  });

  // Prescription initial data (mirrors CertificateFormInner logic)
  const prescriptionInitialData = useMemo((): Partial<PrescriptionFormData> => {
    if (submittedPrescription) {
      return {
        ...submittedPrescription,
        signature: initialFormData.signature || submittedPrescription.signature || '',
      };
    }
    let illnessData: Partial<PrescriptionFormData> = {};
    if (patient?.detail_of_illness) {
      try { illnessData = JSON.parse(patient.detail_of_illness); }
      catch { illnessData = { complaintsOf: patient.detail_of_illness }; }
    }
    return {
      drName: initialFormData.drName || '',
      regNo: initialFormData.regNo || '',
      specialization: initialFormData.specialization || '',
      patientName: initialFormData.patientName || '',
      docId: initialFormData.docId || '',
      ageGender: [initialFormData.age, initialFormData.gender].filter(Boolean).join(' / '),
      issueDate: initialFormData.issueDate || new Date().toISOString().split('T')[0],
      fullDiagnosis: initialFormData.diagnosis || '',
      signature: initialFormData.signature || '',
      ...illnessData,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { formData: rxData } = usePrescriptionForm({
    patientDocId: docId!,
    initialData: prescriptionInitialData,
    submittedData: submittedPrescription ?? undefined,
    persistDraft: false,
  });

  // Load fitStamp for FITNESS CERTIFICATE
  useEffect(() => {
    if (formData.certificateType === 'FITNESS CERTIFICATE') {
      getBase64FromUrl('/fit.png').then((b64) => setField('fitStamp', b64)).catch(() => {});
    } else {
      setField('fitStamp', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.certificateType]);

  // Sync generated content into formData
  const generatedContent = useMemo(
    () => buildCertificateContentBox(formData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      formData.certificateType, formData.gender, formData.drName, formData.patientName,
      formData.guardianName, formData.age, formData.identificationMark, formData.address,
      formData.examTime, formData.examDate, formData.diagnosis, formData.noOfDays,
      formData.fromDate, formData.caretakerFullName, formData.caretakerRelation,
      formData.caretakerAge, formData.he_she, formData.his_her,
    ]
  );

  useEffect(() => {
    if (formData.certificateType && formData.content !== generatedContent) {
      setField('content', generatedContent);
    }
  }, [generatedContent, formData.certificateType, formData.content, setField]);

  // Fetch PDF templates once on mount
  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerate = useCallback(() => {
    if (!certTemplateReady || !patient) return;
    const rxDataForPdf = {
      ...rxData,
      ageGender: [formData.age, formData.gender].filter(Boolean).join(' / ') || rxData.ageGender,
      signature: formData.signature || rxData.signature,
    };
    if (includePrescription && rxTemplateReady) {
      generateMergedPdf(formData, rxDataForPdf, patient.doc_id);
    } else {
      generateCertificatePdf(formData, patient.doc_id);
    }
  }, [
    certTemplateReady, rxTemplateReady, includePrescription, formData, rxData,
    patient, generateCertificatePdf, generateMergedPdf,
  ]);

  if (isLoading) return <Skeleton />;

  if (error || !patient) {
    return (
      <div className="flex flex-col items-center justify-center py-32 gap-4">
        <p className="text-gray-500 dark:text-gray-400 text-base">{error || 'Patient not found.'}</p>
        <button
          onClick={() => navigate('/patients')}
          className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
        >
          <ChevronLeftIcon className="w-4 h-4" />
          Back to Patients
        </button>
      </div>
    );
  }

  const displayName =
    [patient.name, patient.last_name].filter(Boolean).join(' ').trim() ||
    patient.full_name ||
    'Unknown Patient';

  return (
    <div className="space-y-6 pb-16">

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <ChevronLeftIcon className="w-5 h-5" />
          </button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{displayName}</h1>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${STATUS_STYLES[patient.status] || 'bg-gray-100 text-gray-700'}`}>
                {patient.status}
              </span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              #{patient.doc_id} · PDF View
            </p>
          </div>
        </div>

        <Link
          to={`/patients/${patient.doc_id}`}
          className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          <PencilSquareIcon className="w-4 h-4" />
          Edit Form
        </Link>
      </div>

      {/* ── Info cards ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Doctor */}
        <Card title="Doctor">
          <InfoField label="Name" value={formData.drName} />
          <InfoField label="Reg. No." value={formData.regNo} mono />
          <InfoField label="Specialization" value={formData.specialization} />
          {formData.signature && (
            <div className="col-span-2 flex items-center gap-3 p-2 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
              <img src={formData.signature} alt="Signature" className="h-10 object-contain" />
              <p className="text-xs text-gray-400">Signature</p>
            </div>
          )}
        </Card>

        {/* Patient */}
        <Card title="Patient">
          <InfoField label="Full Name" value={formData.patientName} />
          <InfoField label="Age" value={formData.age} />
          <InfoField label="Gender" value={formData.gender} />
          <InfoField label="Address" value={formData.address} />
        </Card>

        {/* Certificate */}
        <Card title="Certificate">
          <InfoField wide label="Certificate Type" value={formData.certificateType} />
          <InfoField label="Issue Date" value={formData.issueDate} />
          <InfoField label="Diagnosis" value={formData.diagnosis} />
          {formData.noOfDays && <InfoField label="No. of Days" value={formData.noOfDays} />}
          {formData.fromDate && <InfoField label="From Date" value={formData.fromDate} />}
          {formData.organisation && <InfoField wide label="Organisation" value={formData.organisation} />}
        </Card>
      </div>

      {/* ── Generate & Download panel ──────────────────────────────── */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
        <div className="flex items-center gap-2 px-6 py-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
          <DocumentArrowDownIcon className="w-4 h-4 text-gray-400" />
          <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
            Generate & Download PDF
          </h3>
        </div>

        <div className="p-6 space-y-5">

          {/* Generate controls */}
          {templateError ? (
            <p className="text-sm text-red-500">{templateError}</p>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => setIncludePrescription((p) => !p)}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
                  includePrescription
                    ? 'bg-emerald-600 border-emerald-600 text-white'
                    : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-emerald-400 hover:text-emerald-600 dark:hover:text-emerald-400'
                }`}
              >
                <ClipboardDocumentCheckIcon className="w-4 h-4" />
                {includePrescription ? 'Prescription included' : 'Include Prescription'}
              </button>

              <button
                type="button"
                onClick={handleGenerate}
                disabled={isGenerating || isLoadingTemplates || !certTemplateReady || (includePrescription && !rxTemplateReady)}
                className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm"
              >
                {isLoadingTemplates ? (
                  <><ArrowPathIcon className="w-4 h-4 animate-spin" />Loading templates…</>
                ) : isGenerating ? (
                  <><ArrowPathIcon className="w-4 h-4 animate-spin" />Generating…</>
                ) : (
                  <><DocumentArrowDownIcon className="w-4 h-4" />
                    {includePrescription ? 'Generate & Merge PDF' : 'Generate PDF'}</>
                )}
              </button>
            </div>
          )}

          {generateError && (
            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
              <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
              {generateError}
            </div>
          )}
          {pendingUpload && uploadState === 'idle' && (
            <div className="flex flex-col gap-2 p-3 rounded-lg border border-blue-200 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/20">
              <p className="text-xs font-medium text-blue-800 dark:text-blue-200">
                PDF downloaded. Save to patient records?
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={confirmUpload}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                >
                  Save to Records
                </button>
                <button
                  type="button"
                  onClick={discardUpload}
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  Discard
                </button>
              </div>
            </div>
          )}
          {uploadState === 'loading' && (
            <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
              <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
              Uploading to records…
            </div>
          )}
          {uploadState === 'success' && (
            <div className="flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400">
              <ClipboardDocumentCheckIcon className="w-3.5 h-3.5" />
              Saved to patient records
            </div>
          )}
          {uploadState === 'error' && (
            <div className="flex flex-col gap-1.5 p-3 rounded-lg border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20">
              <p className="text-xs font-medium text-red-700 dark:text-red-300">
                Upload failed — {uploadError || 'Unknown error'}
              </p>
              <button
                type="button"
                onClick={confirmUpload}
                className="self-start px-3 py-1 text-xs font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors"
              >
                Retry
              </button>
            </div>
          )}

          {/* Saved certificates */}
          <div className="border-t border-gray-100 dark:border-gray-800 pt-5">
            <SavedCertificates docId={patient.doc_id} refreshKey={certRefreshKey} />
          </div>
        </div>
      </div>
    </div>
  );
};
